import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, Typography, TextField, InputLabel,
    RadioGroup, FormControlLabel, Radio, Divider,
    CircularProgress, Chip, Tooltip, IconButton, useMediaQuery,
} from '@mui/material';
import { Copy, Check, UserX, UserCheck, Trash2, Plus, RefreshCw, Share2 } from 'lucide-react';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import CalendarInput from '../ui/calendar-input';
import DeletePopup from './delete';
import DeletePopupMobile from './mobile/delete-mobile';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

interface Viewer {
    id: number;
    email: string;
    isVerified: boolean;
    isActive: boolean;
    verificationSentAt: string | null;
    verifiedAt: string | null;
}

interface FolderData {
    id: number;
    name: string;
    slug: string | null;
    accessType: 'anonymous' | 'email_list' | null;
    expiresAt: string | null;
    viewers: Viewer[];
}

export interface ManageFolderShareProps {
    open: boolean;
    folderId: number | null;
    folderName: string;
    entityId: number | null;
    onClose: () => void;
}

const ManageFolderShare: React.FC<ManageFolderShareProps> = ({ open, folderId, folderName, entityId, onClose }) => {
    const { showSuccess, showError } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;

    const [folder, setFolder] = useState<FolderData | null>(null);
    const [accessType, setAccessType] = useState<'anonymous' | 'email_list'>('anonymous');
    const [expiresAt, setExpiresAt] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [copied, setCopied] = useState(false);
    const [confirmRegenerate, setConfirmRegenerate] = useState(false);
    const [confirmUnshare, setConfirmUnshare] = useState(false);

    const [fetchFolder, setFetchFolder] = useApi<{ success: boolean; folder: FolderData }>();
    const [shareApi, setShareApi] = useApi<{ success: boolean; folder: FolderData }>();
    const [updateShareApi, setUpdateShareApi] = useApi<{ success: boolean; folder: FolderData }>();
    const [unshareApi, setUnshareApi] = useApi();
    const [regenerateApi, setRegenerateApi] = useApi<{ success: boolean; folder: FolderData }>();
    const [addViewerApi, setAddViewerApi] = useApi<{ success: boolean; viewer: Viewer }>();
    const [updateViewerApi, setUpdateViewerApi] = useApi();
    const [removeViewerApi, setRemoveViewerApi] = useApi();
    const [pendingViewerId, setPendingViewerId] = useState<number | null>(null);

    const shareUrl = folder?.slug ? `${window.location.origin}/view/${folder.slug}` : '';
    const isShared = !!folder?.slug;

    useEffect(() => {
        if (!open || !folderId || !entityId) return;
        setFolder(null);
        setNewEmail('');
        setConfirmRegenerate(false);
        setConfirmUnshare(false);
        setFetchFolder(buildApiRequestConfig(routes.documentFolders.manage, [
            { param: 'id', value: folderId },
            { param: 'entityId', value: entityId },
        ]));
    }, [open, folderId, entityId]);

    const applyFolder = (f: FolderData) => {
        setFolder({ ...f, viewers: f.viewers || [] });
        setAccessType(f.accessType || 'anonymous');
        setExpiresAt(f.expiresAt ? f.expiresAt.slice(0, 10) : '');
    };

    useEffect(() => {
        const { data, isLoading } = fetchFolder;
        if (!isLoading && data?.folder) applyFolder(data.folder);
    }, [fetchFolder.data, fetchFolder.isLoading]);

    // ─── Create / start sharing ────────────────────────────────────────────

    const handleShare = useCallback(() => {
        if (!folderId || !entityId) return;
        setShareApi({
            ...buildApiRequestConfig(routes.documentFolders.share, [{ param: 'id', value: folderId }]),
            body: { entityId, accessType },
        });
    }, [folderId, entityId, accessType]);

    useEffect(() => {
        const { data, isLoading, error } = shareApi;
        if (!isLoading && data?.folder) {
            applyFolder(data.folder);
            showSuccess('Folder shared');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to share folder');
    }, [shareApi.data, shareApi.isLoading, shareApi.error]);

    // ─── Save settings ─────────────────────────────────────────────────────

    const handleSave = useCallback(() => {
        if (!folderId || !entityId || !folder) return;
        setUpdateShareApi({
            ...buildApiRequestConfig(routes.documentFolders.updateShare, [{ param: 'id', value: folderId }]),
            body: { entityId, accessType, expiresAt: expiresAt || null },
        });
    }, [folderId, entityId, folder, accessType, expiresAt]);

    useEffect(() => {
        const { data, isLoading, error } = updateShareApi;
        if (!isLoading && data?.folder) {
            setFolder(prev => prev ? { ...prev, accessType: data.folder.accessType, expiresAt: data.folder.expiresAt } : prev);
            showSuccess('Settings saved');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save settings');
    }, [updateShareApi.data, updateShareApi.isLoading, updateShareApi.error]);

    // ─── Regenerate link ───────────────────────────────────────────────────

    const handleRegenerate = useCallback(() => {
        if (!folderId || !entityId) return;
        setConfirmRegenerate(false);
        setRegenerateApi({
            ...buildApiRequestConfig(routes.documentFolders.regenerateShare, [{ param: 'id', value: folderId }]),
            body: { entityId },
        });
    }, [folderId, entityId]);

    useEffect(() => {
        const { data, isLoading, error } = regenerateApi;
        if (!isLoading && data?.folder) {
            setFolder(prev => prev ? { ...prev, slug: data.folder.slug } : prev);
            showSuccess('Shareable link regenerated — old link is now invalid');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to regenerate link');
    }, [regenerateApi.data, regenerateApi.isLoading, regenerateApi.error]);

    // ─── Unshare ───────────────────────────────────────────────────────────

    const handleUnshare = useCallback(() => {
        if (!folderId || !entityId) return;
        setConfirmUnshare(false);
        setUnshareApi({
            ...buildApiRequestConfig(routes.documentFolders.unshare, [{ param: 'id', value: folderId }]),
            body: { entityId },
        });
    }, [folderId, entityId]);

    useEffect(() => {
        const { data, isLoading, error } = unshareApi;
        if (!isLoading && data) {
            setFolder(prev => prev ? { ...prev, slug: null, accessType: null, expiresAt: null, viewers: [] } : prev);
            showSuccess('Folder share revoked');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to revoke share');
    }, [unshareApi.data, unshareApi.isLoading, unshareApi.error]);

    // ─── Viewers ────────────────────────────────────────────────────────────

    const handleAddViewer = useCallback(() => {
        if (!folderId || !entityId || !newEmail.trim()) return;
        setAddViewerApi({
            ...buildApiRequestConfig(routes.documentFolders.addViewer, [{ param: 'id', value: folderId }]),
            body: { entityId, email: newEmail.trim().toLowerCase() },
        });
    }, [folderId, entityId, newEmail]);

    useEffect(() => {
        const { data, isLoading, error } = addViewerApi;
        if (!isLoading && data?.viewer) {
            setFolder(prev => prev ? { ...prev, accessType: 'email_list', viewers: [...prev.viewers, data.viewer] } : prev);
            setAccessType('email_list');
            setNewEmail('');
            showSuccess('Viewer added — verification email sent');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to add viewer');
    }, [addViewerApi.data, addViewerApi.isLoading, addViewerApi.error]);

    const handleToggleViewer = useCallback((viewer: Viewer) => {
        if (!folderId || !entityId) return;
        setPendingViewerId(viewer.id);
        setUpdateViewerApi({
            ...buildApiRequestConfig(routes.documentFolders.updateViewer, [
                { param: 'id', value: folderId },
                { param: 'viewerId', value: viewer.id },
            ]),
            body: { entityId, isActive: !viewer.isActive },
        });
    }, [folderId, entityId]);

    useEffect(() => {
        const { data, isLoading, error } = updateViewerApi;
        if (!isLoading) {
            if (data) {
                setFolder(prev => prev
                    ? { ...prev, viewers: prev.viewers.map(v => v.id === pendingViewerId ? { ...v, isActive: !v.isActive } : v) }
                    : prev
                );
            }
            if (error) showError(typeof error === 'string' ? error : 'Failed to update viewer');
            setPendingViewerId(null);
        }
    }, [updateViewerApi.data, updateViewerApi.isLoading]);

    const handleRemoveViewer = useCallback((viewer: Viewer) => {
        if (!folderId || !entityId) return;
        setPendingViewerId(viewer.id);
        setRemoveViewerApi({
            ...buildApiRequestConfig(routes.documentFolders.removeViewer, [
                { param: 'id', value: folderId },
                { param: 'viewerId', value: viewer.id },
            ]),
            body: { entityId },
        });
    }, [folderId, entityId]);

    useEffect(() => {
        const { data, isLoading, error } = removeViewerApi;
        if (!isLoading) {
            if (data) {
                setFolder(prev => prev ? { ...prev, viewers: prev.viewers.filter(v => v.id !== pendingViewerId) } : prev);
            }
            if (error) showError(typeof error === 'string' ? error : 'Failed to remove viewer');
            setPendingViewerId(null);
        }
    }, [removeViewerApi.data, removeViewerApi.isLoading]);

    // ─── Copy link ─────────────────────────────────────────────────────────

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [shareUrl]);

    const handleClose = useCallback(() => {
        setFolder(null);
        setNewEmail('');
        setExpiresAt('');
        setCopied(false);
        setConfirmRegenerate(false);
        setConfirmUnshare(false);
        onClose();
    }, [onClose]);

    const isLoading = fetchFolder.isLoading;
    const isSharing = shareApi.isLoading;
    const isSaving = updateShareApi.isLoading;
    const isRegenerating = regenerateApi.isLoading;
    const hasChanges = isShared && folder && (
        accessType !== folder.accessType ||
        expiresAt !== (folder.expiresAt ? folder.expiresAt.slice(0, 10) : '')
    );

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Share2 size={20} color="var(--color-primary)" /> Share Folder{folderName ? `: ${folderName}` : ''}
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                {isLoading || !folder ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                        <CircularProgress size={32} sx={{ color: 'var(--color-primary)' }} />
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading…</Typography>
                    </Box>
                ) : !isShared ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                            Sharing this folder grants access to everything inside it — including subfolders — regardless of each document's own individual sharing settings.
                        </Typography>
                        <Box>
                            <InputLabel sx={{ fontSize: 13, mb: 0.5, fontWeight: 600 }}>Who can view</InputLabel>
                            <RadioGroup value={accessType} onChange={e => setAccessType(e.target.value as 'anonymous' | 'email_list')} row>
                                <FormControlLabel value="anonymous" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Anyone with the link</Typography>} />
                                <FormControlLabel value="email_list" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Invited people only</Typography>} />
                            </RadioGroup>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box>
                            <InputLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Shareable Link</InputLabel>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                    fullWidth size="small"
                                    value={shareUrl}
                                    InputProps={{ readOnly: true, sx: { fontSize: 13, color: 'var(--color-text-muted)' } }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                                    <IconButton onClick={handleCopy} size="small"
                                        sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, width: 36, height: 36 }}>
                                        {copied ? <Check size={16} color="var(--color-primary)" /> : <Copy size={16} color="var(--color-text-muted)" />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Regenerate link">
                                    <IconButton
                                        size="small"
                                        onClick={() => setConfirmRegenerate(true)}
                                        disabled={isRegenerating}
                                        sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, width: 36, height: 36 }}
                                    >
                                        {isRegenerating ? <CircularProgress size={14} /> : <RefreshCw size={16} color="var(--color-text-muted)" />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <DeleteModal
                                open={confirmRegenerate}
                                message="Regenerate shareable link?"
                                description="Anyone with the current link will lose access. A new link will be created."
                                confirmLabel="Yes, regenerate"
                                cancelLabel="Cancel"
                                onConfirm={handleRegenerate}
                                onCancel={() => setConfirmRegenerate(false)}
                            />
                        </Box>

                        <Divider />

                        <Box>
                            <InputLabel sx={{ fontSize: 13, mb: 0.5, fontWeight: 600 }}>Who can view</InputLabel>
                            <RadioGroup value={accessType} onChange={e => setAccessType(e.target.value as 'anonymous' | 'email_list')} row>
                                <FormControlLabel value="anonymous" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Anyone with the link</Typography>} />
                                <FormControlLabel value="email_list" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Invited people only</Typography>} />
                            </RadioGroup>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <InputLabel sx={{ fontSize: 13, mb: 0.5, fontWeight: 600 }}>Link expiry (optional)</InputLabel>
                            <CalendarInput value={expiresAt} onChange={setExpiresAt} />
                        </Box>

                        {accessType === 'email_list' && (
                            <>
                                <Divider />
                                <Box>
                                    <InputLabel sx={{ fontSize: 13, mb: 1, fontWeight: 600 }}>
                                        Invited viewers ({folder.viewers.length})
                                    </InputLabel>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField
                                            size="small" fullWidth placeholder="Email address"
                                            value={newEmail}
                                            onChange={e => setNewEmail(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddViewer()}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddViewer}
                                            disabled={!newEmail.trim() || addViewerApi.isLoading}
                                            startIcon={addViewerApi.isLoading
                                                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                                : <Plus size={14} />
                                            }
                                            sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary-hover)' } }}
                                        >
                                            Add
                                        </Button>
                                    </Box>

                                    {folder.viewers.length === 0 ? (
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', py: 1 }}>
                                            No viewers added yet. Add an email above to invite someone.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {folder.viewers.map(viewer => {
                                                const isPending = pendingViewerId === viewer.id;
                                                return (
                                                    <Box key={viewer.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        p: 1, borderRadius: 2,
                                                        background: 'var(--color-container-background)',
                                                        border: '1px solid #f0f0f0',
                                                        opacity: viewer.isActive ? 1 : 0.6,
                                                    }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {viewer.email}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={!viewer.isActive ? 'Revoked' : viewer.isVerified ? 'Verified' : 'Pending'}
                                                            size="small"
                                                            sx={{
                                                                fontSize: 11, height: 20,
                                                                background: !viewer.isActive ? 'var(--color-container-background)' : viewer.isVerified ? '#e8f5e9' : '#fff8e1',
                                                                color: !viewer.isActive ? '#9e9e9e' : viewer.isVerified ? '#388e3c' : '#f57c00',
                                                            }}
                                                        />
                                                        <Tooltip title={viewer.isActive ? 'Revoke access' : 'Restore access'}>
                                                            <span>
                                                                <IconButton size="small" onClick={() => handleToggleViewer(viewer)} disabled={isPending}>
                                                                    {isPending
                                                                        ? <CircularProgress size={14} />
                                                                        : viewer.isActive
                                                                            ? <UserX size={14} color="#e53935" />
                                                                            : <UserCheck size={14} color="var(--color-primary)" />
                                                                    }
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Remove permanently">
                                                            <span>
                                                                <IconButton size="small" onClick={() => handleRemoveViewer(viewer)} disabled={isPending}>
                                                                    <Trash2 size={14} color="#bdbdbd" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}

                        <Divider />
                        <Box>
                            <Button
                                size="small"
                                onClick={() => setConfirmUnshare(true)}
                                sx={{ color: '#e53935', textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                            >
                                Revoke folder share
                            </Button>
                            <DeleteModal
                                open={confirmUnshare}
                                message="Revoke folder share?"
                                description="The link will stop working immediately. Documents inside will fall back to their own individual sharing settings."
                                red
                                confirmLabel="Yes, revoke"
                                cancelLabel="Cancel"
                                onConfirm={handleUnshare}
                                onCancel={() => setConfirmUnshare(false)}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}
                >
                    Close
                </Button>
                {folder && !isShared && (
                    <Button
                        onClick={handleShare}
                        variant="contained"
                        disabled={isSharing}
                        startIcon={isSharing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
                        sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 140, '&:hover': { background: 'var(--color-primary-hover)' } }}
                    >
                        {isSharing ? 'Sharing…' : 'Share Folder'}
                    </Button>
                )}
                {folder && isShared && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={isSaving || !hasChanges}
                        startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
                        sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-primary-hover)' } }}
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                )}
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} heightVh={90}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default ManageFolderShare;
